import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  ListSubheader,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

const FieldMappingEditor = ({
  headers,
  sampleRows,
  mapping,
  targetFields,
  entityType,
  onChange,
  fixedValues = {},
  onFixedValuesChange,
}) => {
  const { t } = useTranslation();

  // Get sample value for a header
  const getSampleValue = (header) => {
    if (!sampleRows || sampleRows.length === 0) return '';
    const firstRow = sampleRows[0];
    return firstRow[header] || '';
  };

  // Handle mapping change for a single header
  const handleMappingChange = (header, targetField) => {
    const newMapping = { ...mapping };
    if (targetField === '') {
      delete newMapping[header];
    } else {
      newMapping[header] = targetField;
    }
    onChange(newMapping);
  };

  // Group target fields for better organization
  const groupedFields = targetFields.reduce((acc, field) => {
    const group = field.group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {});

  // Get group label
  const getGroupLabel = (group) => {
    const labels = {
      company: t('Company'),
      individual: t('Individual'),
      contact: t('Contact'),
      business: t('Business'),
      preferences: t('Preferences'),
      special: t('Special mappings'),
      basic: t('Basic'),
      identification: t('Identification'),
      pricing: t('Pricing'),
      inventory: t('Inventory'),
      vendor: t('Vendor'),
      details: t('Details'),
      other: t('Other'),
    };
    return labels[group] || group;
  };

  // Check if a header is mapped
  const isMapped = (header) => !!mapping[header];

  // Render select options with grouping
  const renderSelectOptions = () => {
    const options = [];

    // Add "Skip" option
    options.push(
      <MenuItem key="__skip__" value="">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <RemoveCircleOutlineIcon fontSize="small" />
          {t('Skip column')}
        </Box>
      </MenuItem>
    );

    // Add grouped options
    Object.entries(groupedFields).forEach(([group, fields]) => {
      options.push(
        <ListSubheader key={`group-${group}`} sx={{ bgcolor: 'background.paper' }}>
          {getGroupLabel(group)}
        </ListSubheader>
      );
      fields.forEach(field => {
        options.push(
          <MenuItem
            key={field.value}
            value={field.value}
            sx={{
              pl: 4,
              ...(field.value.startsWith('_') && {
                fontStyle: 'italic',
                color: 'text.secondary',
              }),
            }}
          >
            {t(field.label)}
          </MenuItem>
        );
      });
    });

    return options;
  };

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell sx={{ fontWeight: 600, width: '30%' }}>
              {t('CSV Column')}
            </TableCell>
            <TableCell sx={{ width: '10%', textAlign: 'center' }}></TableCell>
            <TableCell sx={{ fontWeight: 600, width: '30%' }}>
              {t('Target Field')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: '30%' }}>
              {t('Sample Value')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {headers.map((header) => (
            <TableRow
              key={header}
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
                ...(isMapped(header) && { bgcolor: 'success.50' }),
              }}
            >
              {/* CSV Column */}
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isMapped(header) ? (
                    <CheckCircleIcon fontSize="small" color="success" />
                  ) : (
                    <RemoveCircleOutlineIcon fontSize="small" color="disabled" />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {header}
                  </Typography>
                </Box>
              </TableCell>

              {/* Arrow */}
              <TableCell sx={{ textAlign: 'center' }}>
                <ArrowForwardIcon fontSize="small" color="disabled" />
              </TableCell>

              {/* Target Field Select */}
              <TableCell>
                <FormControl size="small" fullWidth>
                  <Select
                    value={mapping[header] || ''}
                    onChange={(e) => handleMappingChange(header, e.target.value)}
                    displayEmpty
                    sx={{
                      '& .MuiSelect-select': {
                        py: 1,
                      },
                    }}
                  >
                    {renderSelectOptions()}
                  </Select>
                </FormControl>
              </TableCell>

              {/* Sample Value */}
              <TableCell>
                <Tooltip title={getSampleValue(header)} placement="top">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getSampleValue(header) || '-'}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Fixed values section */}
      {onFixedValuesChange && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.default' }}>
            <Typography variant="subtitle2">{t('Fixed values')}</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onFixedValuesChange([...fixedValues, { targetField: '', value: '' }])}
            >
              {t('Add fixed value')}
            </Button>
          </Box>
          {fixedValues.map((entry, idx) => (
            <Box key={idx} sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                size="small"
                placeholder={t('Value')}
                value={entry.value}
                onChange={(e) => {
                  const updated = [...fixedValues];
                  updated[idx] = { ...entry, value: e.target.value };
                  onFixedValuesChange(updated);
                }}
                sx={{ flex: 1 }}
              />
              <ArrowForwardIcon fontSize="small" color="disabled" />
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={entry.targetField}
                  onChange={(e) => {
                    const updated = [...fixedValues];
                    updated[idx] = { ...entry, targetField: e.target.value };
                    onFixedValuesChange(updated);
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <Box sx={{ color: 'text.secondary' }}>{t('Select field')}</Box>
                  </MenuItem>
                  {renderSelectOptions()}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => {
                  const updated = fixedValues.filter((_, i) => i !== idx);
                  onFixedValuesChange(updated);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Mapping summary */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          {Object.keys(mapping).length} / {headers.length} {t('columns mapped')}
        </Typography>
      </Box>
    </TableContainer>
  );
};

export default FieldMappingEditor;
